import exprEval from 'expression-eval';
import mailcheck from 'mailcheck';

import isTest from './helpers/isTest';
import {
  CALENDAR_HOURS,
  SHIFT_TYPE,
  DEFAULT_COUNTRY_ENTRY,
  DEFAULT_LABOR_DATA_KEYS,
  DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT,
  MAIL_CHECK_CONFIG,
} from './constants';
import countries from './countries';

export const arrayToDict = (arr) => arr.reduce((agg, item) => ((agg[item] = true), agg), {});

export default {
  arrayToDict,
  addDays,
  addMonths,
  addYears,
  moveToDayOfWeek,
  normalizeString,
  addHours,
  addMinutes,
  getRealDate,
  getDatesBetween,
  getFullHoursBetween,
  getDaysForPeriod,
  getNowDate,
  getOrgPayrollMonth,
  getMonthWeekDaysCount,
  formatDate,
  formatTime,
  formatShortTime,
  getDayPart,
  addJoin,
  uniq,
  clearObject,
  getId,
  resolveQueryParams,
  ab2str,
  str2ab,
  extractName,
  sortBy,
  getDisplayDuration,
  formatNumber,
  roundNumber,
  floorNumber,
  getValueFromPropertyChain,
  dateFromHours,
  getHourOptions,
  getVisibleHoursRange,
  getVisibleHoursBoundaries,
  getBusinessDayForCalendar,
  getBusinessDayStart,
  isShiftWithinBusinessDay,
  startsWithinBusinessDay,
  getBusinessDaysBetween,
  canBeSeenWithinVisibleHours,
  sum,
  getEventDateEnd,
  getDatesRangeParam,
  raceProofRequest,
  breakDownTimesheetsToGroups,
  subtractDates,
  extendWithProps,
  getFullHour,
  pluralizeTemplate,
  pluralize,
  arrify,
  getCssTransform,
  injectInTemplateIf,
  resolveWithTimeout,
  doDateRangesOverlaps,
  doDateRangesOverlapsLoosely,
  transpose2dTable,
  getGMTOffsetString,
  normalizeTime,
  rangeArray,
  setHours,
  parseTimeString,
  isObject,
  areSetsEqual,
  storeServerSideShiftsLaborData,
  setToArray,
  arrayLikeToArray,
  evaluateExpression,
  addSuffix,
  getCountryEntry,
  getShiftHourOptions,
  getSolarDay,
  getDefaultStartEnd,
  splitCamelCase,
  getSizeInMb,
  setDate,
  replaceArrayElement,
  oneDeepCopy,
  remapEventDayCost,
  getEmailSuggestion,
  dateStringToDayDateString,
  toDateObject,
  toDateString,
};

const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || 9007199254740991; // polyfill for IE

export function addDays(date, value) {
  const initial = getRealDate(date);
  const internal = getRealDate(date);
  internal.setDate(internal.getDate() + value);

  // this is needed because older browsers don't handle DST changes too well
  // especially android is affected when JS debugging is disabled.
  if (formatTime(initial) !== formatTime(internal)) {
    return addHours(getRealDate(date), value * 24);
  }

  return internal;
}

export function addMonths(date, value) {
  let result = getRealDate(date);
  const targetMonth = (((result.getMonth() + value) % 12) + 12) % 12;
  //                  |_____________________________|
  //                                ^
  //                this produces a number in (-12, 12) range
  //                adding 12 to it and taking a modulo 12 again
  //                results in a non-negative month number

  result.setMonth(result.getMonth() + value);
  if (result.getMonth() !== targetMonth) {
    result = addDays(result, -result.getDate());
  }

  return result;
}

export function addYears(date, value) {
  const result = getRealDate(date);
  result.setFullYear(result.getFullYear() + value);
  return result;
}

export function moveToDayOfWeek(date, dayOfWeek, orient) {
  let diff = (dayOfWeek - date.getDay() + 7 * (orient || +1)) % 7;
  const result = addDays(date, diff === 0 ? (diff += 7 * (orient || +1)) : diff);
  return result;
}

export function normalizeString(string) {
  const translate = [
    { from: 'à', to: 'a' },
    { from: 'á', to: 'a' },
    { from: 'ä', to: 'a' },
    { from: 'â', to: 'a' },
    { from: 'å', to: 'a' },
    { from: 'æ', to: 'ae' },
    { from: 'è', to: 'e' },
    { from: 'é', to: 'e' },
    { from: 'ë', to: 'e' },
    { from: 'ê', to: 'e' },
    { from: 'ě', to: 'e' },
    { from: 'ì', to: 'i' },
    { from: 'í', to: 'i' },
    { from: 'ï', to: 'i' },
    { from: 'î', to: 'i' },
    { from: 'ò', to: 'o' },
    { from: 'ó', to: 'o' },
    { from: 'ö', to: 'o' },
    { from: 'ô', to: 'o' },
    { from: 'ù', to: 'u' },
    { from: 'ú', to: 'u' },
    { from: 'ü', to: 'u' },
    { from: 'û', to: 'u' },
    { from: 'ñ', to: 'n' },
    { from: 'ç', to: 'c' },
    { from: 'č', to: 'c' },
    { from: 'ř', to: 'r' },
    { from: 'š', to: 's' },
    { from: 'ý', to: 'y' },
    { from: 'ž', to: 'z' },
    { from: 'ď', to: 'd' },
    { from: 'ť', to: 't' },
    { from: 'þ', to: 'th' },
    { from: 'ð', to: 'd' },
  ];

  function replaceLetter(text, translation) {
    return text.replace(new RegExp(translation.from, 'g'), translation.to);
  }

  return translate.reduce(replaceLetter, string.toLowerCase());
}

export function addHours(date, value) {
  let internal = new Date(date);
  internal = new Date(internal.getTime() + value * 60 * 60 * 1000);
  return internal;
}

export function addMinutes(date, value) {
  const internal = new Date(date);
  return new Date(internal.getTime() + value * 60 * 1000);
}

export function getRealDate(dateString) {
  if (typeof dateString === 'string') {
    if (dateString.length === 10) {
      // match: 2017-01-01
      const [, year, month, day] = dateString.match(/(\d{4})-(\d{2})-(\d{2})/); // eslint-disable-line
      const result = new Date(Number(year), Number(month) - 1, Number(day));

      return ignoreDaylightSavingOnDate(result, 0);
    }

    // match: 2017-01-01T12:00:00, 2017-01-01 12:00
    const [, year, month, day, hour, minute] = dateString.match(
      /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/
    );
    const targetHour = Number(hour);

    const result = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      targetHour,
      Number(minute)
    );
    return ignoreDaylightSavingOnDate(result, targetHour);
  }

  return new Date(dateString);
}

export function ignoreDaylightSavingOnDate(date, targetHour) {
  const dateHour = date.getHours();
  const _targetHour = targetHour === 24 ? 0 : targetHour;

  if (dateHour !== _targetHour) {
    let hourDifference = Math.abs(dateHour - _targetHour);
    if (hourDifference === 23) {
      hourDifference = dateHour > _targetHour ? -1 : 1;
    }
    const hourDifferenceInMs = hourDifference * 1000 * 3600;
    const timestamp = date.getTime();
    return dateHour > _targetHour
      ? new Date(timestamp - hourDifferenceInMs)
      : new Date(timestamp + hourDifferenceInMs);
  }

  return date;
}

export function getDatesBetween(startDate, stopDate, strictCheck = false) {
  let start = getRealDate(startDate);
  start = getRealDate(`${formatDate(start)}T00:00:00`);

  let stop = getRealDate(stopDate);
  if (strictCheck && formatTime(stop) === '0:00') {
    stop = addDays(stop, -1);
  }

  const dateArray = [];
  let currentDate = start;

  while (currentDate <= stop) {
    dateArray.push(new Date(currentDate));
    currentDate = addDays(currentDate, 1);
  }

  return uniq(dateArray, (date) => formatDate(date));
}

export function getFullHoursBetween(startDate, stopDate) {
  return Math.floor(Math.abs(stopDate.getTime() - startDate.getTime()) / (1000 * 3600));
}

function _getPayrollDaysForPeriod(
  day,
  monthStartingDay,
  { payrollPeriodType = 'MONTHLY', payrollPeriodStartDate, payrollPeriodStartDateTwo }
) {
  const days = [];
  let tempDate;
  let weeksFromStart;
  let counter;

  const dayOne = payrollPeriodStartDate ? payrollPeriodStartDate.getDate() : monthStartingDay;
  const weekday = payrollPeriodStartDate && payrollPeriodStartDate.getDay();
  const dayTwo = payrollPeriodStartDateTwo && payrollPeriodStartDateTwo.getDate();

  switch (payrollPeriodType) {
    case 'MONTHLY':
      tempDate = getRealDate(day);

      while (tempDate.getDate() !== dayOne) {
        tempDate = addDays(tempDate, -1);
      }

      do {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      } while (tempDate.getDate() !== dayOne);

      return days;
    case 'SEMI_MONTHLY':
      tempDate = getRealDate(day);

      while (![dayOne, dayTwo].includes(tempDate.getDate())) {
        tempDate = addDays(tempDate, -1);
      }

      do {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      } while (![dayOne, dayTwo].includes(tempDate.getDate()));

      return days;
    case 'WEEKLY':
      tempDate = getRealDate(day);

      while (tempDate.getDay() !== weekday) {
        tempDate = addDays(tempDate, -1);
      }

      do {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      } while (tempDate.getDay() !== weekday);

      return days;
    case 'BI_WEEKLY':
      tempDate = getRealDate(day);

      while (tempDate.getDay() !== weekday) {
        tempDate = addDays(tempDate, -1);
      }

      weeksFromStart = subtractDates(tempDate, payrollPeriodStartDate, 'days') / 7;
      if (Math.abs(weeksFromStart) % 2) {
        tempDate = addDays(tempDate, -7);
      }

      counter = 14;
      while (counter--) {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      }

      return days;
    default:
      if (!isTest) {
        console.error(`Unknown payroll period type: ${payrollPeriodType}`);
      }

      throw `Unknown payroll period type: ${payrollPeriodType}`;
  }
}

export function getDaysForPeriod(
  period,
  day,
  weekStartingDay = 1,
  monthStartingDay = 1,
  payrollConfig = {}
) {
  /*
    Get an array of date strings representing a selected period (week or month)
    including the day specified as the "day" parameter
  */
  switch (period) {
    case 'week': {
      let tempDate = getRealDate(day);
      if (tempDate.getDay() !== weekStartingDay) {
        tempDate = moveToDayOfWeek(tempDate, weekStartingDay, -1);
      }

      const days = [formatDate(tempDate)];
      for (let i = 1; i < 7; i++) {
        tempDate = addDays(tempDate, 1);
        days.push(formatDate(tempDate));
      }

      return days;
    }
    case 'month': {
      let tempDate = getRealDate(day);

      while (tempDate.getDate() !== 1) {
        tempDate = addDays(tempDate, -1);
      }

      const days = [];
      do {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      } while (tempDate.getDate() !== 1);

      return days;
    }
    case 'payroll': {
      return _getPayrollDaysForPeriod(day, monthStartingDay, payrollConfig);
    }
    case 'quarter': {
      const dayDate = getRealDate(day);
      const dayMonth = dayDate.getMonth();

      const months = [-2, -1, 0, 1, 2].slice(2 - (dayMonth % 3), 5 - (dayMonth % 3));
      // I know, neat AF ;)

      return months.reduce(
        (range, offset) => [
          ...range,
          ...getDaysForPeriod('month', addMonths(day, offset), undefined, monthStartingDay),
        ],
        []
      );
    }
    case 'year': {
      let tempDate = getRealDate(day);
      tempDate.setDate(1);
      tempDate.setMonth(0);

      const currentYear = tempDate.getFullYear();
      const days = [];
      while (tempDate.getFullYear() !== currentYear + 1) {
        days.push(formatDate(tempDate));
        tempDate = addDays(tempDate, 1);
      }

      return days;
    }
    default:
      throw new Error('Invalid period for utils.getDaysForPeriod');
  }
}

export function getNowDate() {
  return new Date(); // easy to stub in tests
}

export function getOrgPayrollMonth(date, firstDayOfMonth = 1) {
  const gregorianMonth = date.getMonth();
  return date.getDate() >= firstDayOfMonth ? gregorianMonth : (gregorianMonth - 1 + 12) % 12;
}

export function getMonthWeekDaysCount(date, firstDayOfMonth = 1) {
  const copy = new Date(date);
  copy.setDate(firstDayOfMonth);
  let days = 0;
  do {
    if ([1, 2, 3, 4, 5].includes(copy.getDay())) {
      days++;
    }

    copy.setDate(copy.getDate() + 1);
  } while (copy.getDate() !== firstDayOfMonth);

  return days;
}

export function formatDate(date = new Date()) {
  const year = `0000${date.getFullYear()}`.slice(-4);
  const month = `00${date.getMonth() + 1}`.slice(-2);
  const day = `00${date.getDate()}`.slice(-2);

  return `${year}-${month}-${day}`;
}

export function formatTime(date = new Date(), usTimeFormat, padHours) {
  let hour = date.getHours();
  const minute = `00${date.getMinutes()}`.slice(-2);
  let amPm;

  if (usTimeFormat) {
    amPm = hour >= 12 ? 'PM' : 'AM';
    if (hour > 12) {
      hour -= 12;
    }

    if (hour === 0) {
      hour = 12;
    }
  }

  if (padHours) {
    hour = `00${hour}`.slice(-2);
  }

  if (usTimeFormat) {
    return `${hour}:${minute} ${amPm}`;
  }

  return `${hour}:${minute}`;
}

export function formatShortTime(...args) {
  return formatTime(...args)
    .replace(' AM', 'A')
    .replace(' PM', 'P');
}

export function getDayPart() {
  const now = new Date();
  const hour = now.getHours();

  if (hour < 6) {
    return 'night';
  }

  if (hour >= 6 && hour < 12) {
    return 'morning';
  }

  if (hour >= 12 && hour < 18) {
    return 'afternoon';
  }

  return 'evening';
}

export function addJoin(array = []) {
  if (!array.length) {
    return '';
  }

  if (array.length === 1) {
    return array[0];
  }

  return `${array.slice(0, -1).join(', ')} and ${array[array.length - 1]}`;
}

export function uniq(array, cmp = (x) => String(x)) {
  const dict = {};
  array.forEach((item) => {
    const key = cmp(item);
    if (!(key in dict)) {
      dict[key] = item;
    }
  });

  return Object.keys(dict).map((item) => dict[item]);
}

export function clearObject(obj) {
  Object.keys(obj).forEach((key) => {
    delete obj[key];
  });

  return obj;
}

export function getId(str) {
  const idAsNumber = Number(str);
  return Number.isNaN(idAsNumber) ? str : idAsNumber;
}

export function resolveQueryParams(params = {}) {
  const stringified = Object.keys(params)
    .map((key) => {
      if (typeof params[key] === 'undefined') {
        return false;
      }

      if (Array.isArray(params[key])) {
        return params[key].map((value) => `${key}=${encodeURIComponent(value)}`).join('&');
      }

      return `${key}=${encodeURIComponent(params[key])}`;
    })
    .filter((p) => p)
    .join('&');

  return stringified ? `?${stringified}` : '';
}

// Convert ArrayBuffer to String
export function ab2str(buf) {
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(buf);
}

// Convert String to ArrayBuffer
export function str2ab(str) {
  const encoder = new TextEncoder('utf-8');
  return encoder.encode(str).buffer;
}

export function extractName(fullname, showLastNameFirst) {
  const _fullname = fullname.split(' ');
  let name;
  let lastname;

  if (showLastNameFirst) {
    name = _fullname.pop();
    if (!_fullname.length) {
      lastname = [name];
      name = '';
    } else {
      lastname = _fullname;
    }
  } else {
    [name, ...lastname] = _fullname;
  }

  lastname = lastname.length ? lastname.join(' ') : '';
  return { name, lastname };
}

export function sortBy(array, _props, defaultValue) {
  function compare(_a, _b, prop) {
    const a = getValueFromPropertyChain(_a, prop, defaultValue);
    const b = getValueFromPropertyChain(_b, prop, defaultValue);

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b);
    }

    return a - b;
  }

  const props = Array.isArray(_props) ? _props : [_props];
  return array.sort((a, b) => {
    let comparisonResult = 0;
    for (let i = 0; i < props.length && comparisonResult === 0; i++) {
      comparisonResult = compare(a, b, props[i]);
    }

    return comparisonResult;
  });
}

export function getDisplayDuration(
  _durationInMinutes,
  decimalFormat = DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT,
  short = false,
  allowNegative = false,
  showExplicitSign = false
) {
  if (_durationInMinutes === 0) {
    return '';
  }

  const sign = _durationInMinutes < 0 ? '-' : '+';
  if (_durationInMinutes < 0 && !allowNegative) {
    return '';
  }

  const durationInMinutes = Math.abs(_durationInMinutes);

  const durationHours = Math.floor(durationInMinutes / 60);
  const durationMinutes = durationInMinutes % 60;

  const hourUnit = short ? 'H' : 'h';
  const minuteUnit = short ? 'M' : 'min';
  const space = short ? '' : ' ';

  let displayDuration = '';
  if (durationHours) {
    displayDuration = `${formatNumber(durationHours, decimalFormat, false)}${hourUnit}`;
  }

  if (durationMinutes) {
    displayDuration += `${durationHours ? space : ''}${formatNumber(
      durationMinutes,
      decimalFormat,
      false
    )}${minuteUnit}`;
  }

  return `${showExplicitSign || sign === '-' ? sign : ''}${displayDuration}`;
}

export function formatNumber(
  amount,
  decimalFormat = DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT,
  isFractionAlwaysVisible = true
) {
  const isThousandsCommaDelimiter = decimalFormat === DECIMAL_FORMAT_THOUSANDS_COMMA_DECIMAL_DOT;
  const fractionDelimiter = isThousandsCommaDelimiter ? '.' : ',';
  const thousandsDelimiter = isThousandsCommaDelimiter ? ',' : '.';

  let [number, fraction] = amount.toString().split('.'); // eslint-disable-line
  let sign = '';
  if (number[0] === '-') {
    sign = number[0];
    number = number.substr(1);
  }

  number = number
    .split('')
    .reverse()
    .map((digit, index, array) =>
      (index + 1) % 3 === 0 && index < array.length - 1 ? thousandsDelimiter + digit : digit
    )
    .reverse()
    .join('');

  if (!fraction) {
    fraction = '';
  }

  fraction = `${fraction}00`.slice(0, 2);

  if (!isFractionAlwaysVisible && fraction === '00') {
    fraction = '';
  }

  return sign + number + (fraction ? fractionDelimiter + fraction : '');
}

export function roundNumber(number, decimalPoints = 2) {
  const factor = Math.pow(10, decimalPoints);
  return Math.round(number * factor) / factor;
}

export function floorNumber(number, decimalPoints = 2) {
  const factor = Math.pow(10, decimalPoints);
  return Math.floor(number * factor) / factor;
}

export function getValueFromPropertyChain(object, propertyChainOrFn, defaultValue = undefined) {
  if (!object || !propertyChainOrFn) {
    return defaultValue;
  }

  let result;
  if (typeof propertyChainOrFn === 'function') {
    result = propertyChainOrFn(object);
  } else {
    result = propertyChainOrFn
      .split('.')
      .reduce((value, property) => value && value[property], object);
  }
  return typeof result !== 'undefined' ? result : defaultValue;
}

export function dateFromHours(hours, date = new Date()) {
  return getRealDate(`${typeof date === 'string' ? date : formatDate(date)} ${hours}`);
}

export function getHourOptions(start, end = false, startHour = 0, startMinute = 0) {
  let date = start ? new Date(start) : new Date();

  if (end) {
    date.setMinutes(date.getMinutes() - (date.getMinutes() % 15) + 15);
  } else {
    date.setHours(startHour);
    date.setMinutes(startMinute);
  }

  const res = [];
  while (res.length < 96) {
    // 96 is the number of 15-minutes periods in a day
    const value = new Date(date);
    res.push({
      value,
      label: date,
    });
    date = new Date(date.getTime() + 15 * 60 * 1000);
  }

  return res;
}

export function getVisibleHoursRange(start, end) {
  const { visibleStart, visibleEnd } = getVisibleHoursBoundaries(start, end);
  const visibleStartHour = visibleStart.getHours();
  const visibleEndMinute = visibleEnd.getMinutes();
  let visibleEndHour = visibleEndMinute ? visibleEnd.getHours() : visibleEnd.getHours() - 1;
  visibleEndHour = visibleEndHour < 0 ? 24 : visibleEndHour;

  if (visibleStart.getDate() === visibleEnd.getDate() || visibleEndHour === 24) {
    return CALENDAR_HOURS.filter((hour) => hour >= visibleStartHour && hour <= visibleEndHour);
  }

  return [
    ...CALENDAR_HOURS.filter((hour) => hour >= visibleStartHour),
    ...CALENDAR_HOURS.filter((hour) => hour <= visibleEndHour),
  ];
}

export function getVisibleHoursBoundaries(
  visibleHoursStart,
  visibleHoursEnd,
  dtstart = dateFromHours(visibleHoursStart)
) {
  let visibleStart = dateFromHours(visibleHoursStart, dtstart);
  let visibleEnd = dateFromHours(visibleHoursEnd, dtstart);

  if (visibleEnd.getTime() <= visibleStart.getTime()) {
    const startHour = dtstart.getHours();
    if (startHour <= visibleEnd.getHours() && startHour !== visibleStart.getHours()) {
      visibleStart = addDays(visibleStart, -1);
    } else {
      visibleEnd = addDays(visibleEnd, 1);
    }
  }

  return { visibleStart, visibleEnd };
}

export function getBusinessDayForCalendar(visibleHoursStart, visibleHoursEnd, date) {
  const start = dateFromHours(visibleHoursStart);
  const end = dateFromHours(visibleHoursEnd);
  let visibleStart = getRealDate(date);
  let visibleEnd = getRealDate(date);

  if (start.getTime() >= end.getTime()) {
    visibleEnd = addDays(visibleEnd, 1);
  }

  // dst safe way to set hours
  visibleStart = getRealDate(`${formatDate(visibleStart)}T${formatTime(start, false, true)}:00`);
  visibleEnd = getRealDate(`${formatDate(visibleEnd)}T${formatTime(end, false, true)}:00`);
  return { visibleStart, visibleEnd };
}

export function getBusinessDayStart(visibleHoursStart, visibleHoursEnd, date) {
  return getVisibleHoursBoundaries(visibleHoursStart, visibleHoursEnd, date).visibleStart;
}

export function isShiftWithinBusinessDay(visibleHoursStart, visibleHoursEnd, date, shift) {
  const { dtstart } = shift;
  const dtendDisplay = getEventDateEnd(shift);
  const { visibleStart, visibleEnd } = getBusinessDayForCalendar(
    visibleHoursStart,
    visibleHoursEnd,
    date
  );
  return dtstart < visibleEnd && dtendDisplay > visibleStart;
}

export function startsWithinBusinessDay(visibleHoursStart, visibleHoursEnd, date, shift) {
  const { dtstart } = shift;
  const { visibleStart, visibleEnd } = getBusinessDayForCalendar(
    visibleHoursStart,
    visibleHoursEnd,
    date
  );
  return dtstart >= visibleStart && dtstart < visibleEnd;
}

export function getBusinessDaysBetween(visibleHoursStart, visibleHoursEnd, event, relevantDays) {
  const eventDtstart = toDateObject(event.dtstart);
  const eventDtend = toDateObject(event.dtend);

  const DAY_MINUTES = 60 * 24;
  const parseVisibleHour = (visibleHourString) =>
    Number(visibleHourString.slice(0, 2)) * 60 + Number(visibleHourString.slice(3, 5));

  const leftBoundaryMinutes = parseVisibleHour(visibleHoursStart);
  let rightBoundaryMinutes = parseVisibleHour(visibleHoursEnd);

  if (rightBoundaryMinutes <= leftBoundaryMinutes) {
    rightBoundaryMinutes += DAY_MINUTES;
  }

  const getMinutesFromDate = (date) => date.getHours() * 60 + date.getMinutes();
  const dtstartMinutes = getMinutesFromDate(eventDtstart);
  let dtendMinutes =
    getMinutesFromDate(eventDtend) +
    (dateFromHours('00:00', eventDtend) - dateFromHours('00:00', eventDtstart)) / 1000 / 60;

  if (event.openEnd) {
    dtendMinutes += 240;
  }

  let dayEndMinutes;
  let dayStartMinutes = -1;

  const offsetsInRange = [];

  let offsetStart = -1;
  let maxOffset = MAX_SAFE_INTEGER;

  if (relevantDays) {
    offsetStart = Math.max(
      Math.round(
        (getRealDate(relevantDays[0]) - dateFromHours('00:00:00', eventDtstart)) /
          1000 /
          60 /
          DAY_MINUTES
      ) - 1,
      -1
    );

    maxOffset =
      Math.round(
        (getRealDate(relevantDays[relevantDays.length - 1]) -
          dateFromHours('00:00:00', eventDtstart)) /
          1000 /
          60 /
          DAY_MINUTES
      ) + 1;
  }

  for (let offset = offsetStart; dayStartMinutes < dtendMinutes && offset <= maxOffset; offset++) {
    dayStartMinutes = leftBoundaryMinutes + offset * DAY_MINUTES;
    dayEndMinutes = rightBoundaryMinutes + offset * DAY_MINUTES;

    if (dayStartMinutes < dtendMinutes && dayEndMinutes > dtstartMinutes) {
      offsetsInRange.push(offset);
    }
  }

  return offsetsInRange.map((offset) => addDays(eventDtstart, offset)).map(formatDate);
}

export function canBeSeenWithinVisibleHours(
  visibleHoursStart,
  visibleHoursEnd,
  { dtstart, dtend, fullDay }
) {
  if (fullDay) {
    return true;
  }
  const visibleHours = getVisibleHoursRange(visibleHoursStart, visibleHoursEnd);
  visibleHours.sort((a, b) => a - b);

  const visibleStartHour = visibleHours[0];
  let visibleEndHour = visibleHours[visibleHours.length - 1] + 1;

  const startMinutes = dtstart.getMinutes();
  let endHours = dtend.getHours();
  let startHours = dtstart.getHours();
  if (startMinutes) {
    startHours++;
  }

  if (startHours >= endHours) {
    endHours += 24;
  }

  if (visibleStartHour >= visibleEndHour) {
    visibleEndHour += 24;
  }

  return startHours < visibleEndHour && endHours > visibleStartHour;
}

export function sum(array = []) {
  return array.reduce((result, item) => result + item, 0);
}

export function getEventDateEnd(event) {
  return event.openEnd ? addHours(event.dtend, 4) : event.dtend;
}

export function getDatesRangeParam(from, to, ignoreTimezone = false) {
  const fromStr = formatDate(from);
  const toStr = formatDate(to);

  if (ignoreTimezone) {
    return `${fromStr}T00:00:00/${toStr}T00:00:00`;
  }

  const [, timezoneOffset] = new Date().toString().match(/GMT([+-]\d{4})/);
  return `${fromStr}T00:00:00${timezoneOffset}/${toStr}T00:00:00${timezoneOffset}`;
}

const raceProofRequestPromises = Object.create(null);
export async function raceProofRequest(promise, id = 'default') {
  raceProofRequestPromises[id] = promise;
  const result = await promise;

  if (promise !== raceProofRequestPromises[id]) {
    if (!isTest) {
      console.info('is test', isTest);
      console.warn('Stale promise resolved');
    }

    throw 'Stale promise resolved';
  }

  return result;
}

export function breakDownTimesheetsToGroups(timesheets) {
  const orderedByBaseSeq = sortBy(timesheets, 'baseSequenceId');
  let lastBaseSeqId;
  const groups = orderedByBaseSeq.reduce((timesheetGroups, timesheet) => {
    if (!lastBaseSeqId || lastBaseSeqId !== timesheet.baseSequenceId) {
      timesheetGroups.push([]);
    }

    lastBaseSeqId = timesheet.baseSequenceId;

    timesheetGroups[timesheetGroups.length - 1].push(timesheet);
    return timesheetGroups;
  }, []);

  return groups.map((group) => sortBy(group, 'timestamp', 'sequenceId'));
}

export function subtractDates(d1, d2, outputUnit) {
  const DSTOffset = d1.getTimezoneOffset() - d2.getTimezoneOffset();
  const msDiff = d1 - d2 - DSTOffset * 60 * 1000;

  if (!outputUnit) {
    return msDiff;
  }

  const sDiff = msDiff / 1000;
  const minDiff = sDiff / 60;
  const hDiff = minDiff / 60;
  const dayDiff = hDiff / 24;

  switch (outputUnit) {
    case 'seconds':
      return sDiff;
    case 'minutes':
      return minDiff;
    case 'hours':
      return hDiff;
    case 'days':
      return dayDiff;
    default:
      throw new Error(`Unknown output unit: ${outputUnit}`);
  }
}

export function extendWithProps(source, target, props, noOverride = false) {
  props.forEach((prop) => {
    if (!(prop in target) || !noOverride) {
      target[prop] = source[prop];
    }
  });

  return target;
}

export function getFullHour(_hour) {
  /**
   * Input: "9"
   * Output: "09:00"
   */

  let hour = String(_hour);
  if (hour.length === 1) {
    hour = `0${hour}`;
  }

  return `${hour}:00`;
}

export function pluralizeTemplate(isPlural, strings, ...values) {
  return strings.reduce(
    (acc, str, i) => acc + str + (values[i] || '') + (isPlural && values[i] ? 's' : ''),
    ''
  );
}

export function pluralize(arrayOrCount, word, wordPlural) {
  if (!word) {
    return word;
  }

  const count = Array.isArray(arrayOrCount) ? arrayOrCount.length : arrayOrCount;

  if (count !== 1 && wordPlural) {
    return wordPlural;
  }

  const pluralSuffix = word.endsWith('s') ? 'es' : 's';
  return `${word}${count === 1 ? '' : pluralSuffix}`;
}

export function arrify(arrayOrNotArray) {
  return Array.isArray(arrayOrNotArray) ? arrayOrNotArray : [arrayOrNotArray];
}

export function getCssTransform(cssValue) {
  return ['-webkit-transform', 'transform'].reduce((acc, prop) => {
    acc[prop] = cssValue;
    return acc;
  }, {});
}

export function injectInTemplateIf(condition) {
  return (strings, ...values) =>
    strings.reduce((acc, str, i) => acc + str + (condition && values[i] ? values[i] : ''), '');
}

export function resolveWithTimeout(duration = 0) {
  if (isTest) {
    return;
  }

  return new Promise((resolve) => setTimeout(resolve, duration));
}

export function doDateRangesOverlaps(range1, range2) {
  return range1.from <= range2.to && range2.from <= range1.to;
}

export function doDateRangesOverlapsLoosely(range1, range2) {
  return range1.from < range2.to && range2.from < range1.to;
}

export function transpose2dTable(rows) {
  if (!rows.map((r) => r.length).every((len) => len === rows[0].length)) {
    throw 'cannot transpose - rows have different lengths';
  }

  const transposed = [];
  rows[0].forEach(() => {
    transposed.push([]);
  });

  rows.forEach((row) => {
    row.forEach((cell, ci) => {
      transposed[ci].push(cell);
    });
  });

  return transposed;
}

export function getGMTOffsetString(dateString) {
  if (typeof dateString !== 'string' || !/[+-]\d{2}:\d{2}$/.test(dateString)) {
    throw new Error('getGMTOffsetString should be called with an ISO date string with timezone');
  }

  const [, sign, hourOffset] = dateString.match(/([+-])(\d{2}):\d{2}$/);
  return `GMT${sign}${Number(hourOffset)}`;
}

export function normalizeTime(date) {
  const n = new Date(date);
  n.setDate(1);
  n.setMonth(0);
  n.setFullYear(1970);
  return n;
}

export function rangeArray(from, to) {
  /**
   * e.g. rangeArray(2, 5) returns [2, 3, 4, 5]
   */
  return Array.from({ length: to - from }).map((__, i) => i + from);
}

export function setHours(date, hour, minute = null, second = null, millisecond = null) {
  const hoursToSet = getPaddedNumber(hour);
  const minutesToSet = getPaddedNumber(minute !== null ? minute : date.getMinutes());
  const secondsToSet = getPaddedNumber(second !== null ? second : date.getSeconds());
  const millisecondsToSet = getPaddedNumber(
    millisecond !== null ? millisecond : date.getMilliseconds()
  );

  const dateString = formatDate(date);
  return getRealDate(
    `${dateString}T${hoursToSet}:${minutesToSet}:${secondsToSet}:${millisecondsToSet}`
  );
}

function getPaddedNumber(number) {
  return number > 9 ? number : `0${number}`;
}

export function parseTimeString(input, usTimeFormat) {
  /**
   * parses valid time strings into form acceptable by JS:
   * e.g. '6am' -> '6:00 AM'
   */
  const isValid = usTimeFormat ? /^\d{1,2}(:\d{2})? ?[AP]M$/i : /^\d{1,2}(:\d{2})?$/;
  if (!isValid.test(input)) {
    return null;
  }

  let parsed;

  if (usTimeFormat) {
    parsed = input.replace(
      /^(\d{1,2})(:\d{2})?( )?([AP]M)$/i,
      (match, g1, g2, g3, g4) => `${g1}${g2 || ':00'} ${g4}`
    );
  } else {
    parsed = input.replace(/^(\d{1,2})(:\d{2})?$/i, (match, g1, g2) => `${g1}${g2 || ':00'}`);
  }

  if (new Date(`2015/12/06 ${parsed}`).toString() === 'Invalid Date') {
    // 2015/12/06 is just an example of a valid date string
    // we need to check whether the Date constructor called with
    // a valid date string + our time string produces a valid date
    return null;
  }

  return parsed;
}

export function isObject(arg) {
  return typeof arg === 'object' && arg !== null && !Array.isArray(arg);
}

export function areSetsEqual(a, b) {
  return a.size === b.size && [...a].every((value) => b.has(value));
}

export function storeServerSideShiftsLaborData(events, costs) {
  events.forEach((event) => {
    if (event.type === SHIFT_TYPE && costs[event.id]) {
      event.__laborDataKeys = Object.keys(costs[event.id][0]);
      event.__laborDataKeys.forEach((prop) => {
        event[`${prop}_array`] = costs[event.id].map((item) => item[prop]);
      });
    }
  });
}

export function setToArray(set) {
  const res = [];
  for (const item of set) {
    res.push(item);
  }

  return res;
}

export function arrayLikeToArray(arrayLike) {
  const res = [];
  for (let i = 0; i < arrayLike.length; i++) {
    res.push(arrayLike[i]);
  }

  return res;
}

const _expressionCache = {};
function _parseExpression(expression) {
  if (!(expression in _expressionCache)) {
    _expressionCache[expression] = exprEval.parse(expression);
  }

  return _expressionCache[expression];
}

export function evaluateExpression(expression, ctx) {
  ctx.ROUND_DECIMAL = (number, precision) => roundNumber(number, precision);
  ctx.NUMBER = (value) => Number(value);

  return exprEval.eval(_parseExpression(expression), ctx);
}

function _remapEvent(event, _sum) {
  (event.__laborDataKeys || DEFAULT_LABOR_DATA_KEYS).forEach((prop) => {
    const arrProp = `${prop}_array`;
    if (event[arrProp]) {
      if ('index' in event && !_sum) {
        event[prop] = event[arrProp].length > event.index ? event[arrProp][event.index] : 0;
      } else {
        event[prop] = event[arrProp].reduce((agg, val) => agg + val, 0);
      }
    }
  });

  return event;
}

export function remapEventDayCost(event, { sum: _sum = false, cache } = {}) {
  if (cache) {
    const cacheKey = `${event.id}_${event.index}_${sum}`;
    if (!(cacheKey in cache)) {
      cache[cacheKey] = _remapEvent(event, _sum);
    }

    return cache[cacheKey];
  }

  _remapEvent(event, _sum);
  return event;
}

export function addSuffix(string) {
  if (!string) {
    return string;
  }

  return /s$/i.test(string) ? `${string}'` : `${string}'s`;
}

export function getCountryEntry(countryCode, countryISOCode, countriesDict = countries) {
  const normalizedCode =
    typeof countryCode === 'string' && countryCode.startsWith('+')
      ? countryCode
      : `+${countryCode}`;
  let result;

  if (countryCode && countryISOCode) {
    result = countriesDict.find((c) => c.value === normalizedCode && c.code === countryISOCode);
  }

  if (!result && countryCode) {
    result = countriesDict.find((c) => c.value === normalizedCode);
  }

  const { value, code } = DEFAULT_COUNTRY_ENTRY;
  return result || countriesDict.find((c) => c.value === value && c.code === code);
}

export function getShiftHourOptions(
  shift,
  { visibleHoursStart = '00:00:00', visibleHoursEnd = '00:00:00' } = {},
  usTimeFormat
) {
  const { visibleStart, visibleEnd } = getVisibleHoursBoundaries(
    visibleHoursStart,
    visibleHoursEnd,
    shift.dtstart
  );
  let startHourOptions = getHourOptions(
    visibleStart,
    false,
    visibleStart.getHours(),
    visibleStart.getMinutes()
  ).map(timeFormatLabels);
  let endHourOptions = getHourOptions(
    shift.dtstart,
    true,
    visibleStart.getHours(),
    visibleStart.getMinutes()
  ).map(timeFormatLabels);
  const numberOfVisibleHours = getFullHoursBetween(visibleStart, visibleEnd);

  if (numberOfVisibleHours >= 24) {
    // DST days have more than 24 days :)
    return { startHourOptions, endHourOptions };
  }

  startHourOptions = startHourOptions.filter(
    (option) => option.value >= visibleStart && option.value <= visibleEnd
  );

  const lastOption = startHourOptions[startHourOptions.length - 1].value;

  if (lastOption.getTime() === visibleEnd.getTime()) {
    startHourOptions.pop(); // make sure that shift is 15 min long at least
  }

  endHourOptions = endHourOptions.filter(
    (option) => option.value >= visibleStart && option.value <= visibleEnd
  );

  return { startHourOptions, endHourOptions };

  function timeFormatLabels(item) {
    item.label = formatTime(item.label, usTimeFormat);
    return item;
  }
}

export function getSolarDay(day, hour, { visibleHoursStart, visibleHoursEnd }) {
  if (!hour && hour !== 0) {
    // we care about business day only on day view (when hour is provided).
    return day;
  }

  const date = getRealDate(day);
  date.setHours(hour, 0, 0, 0);

  const startHour = dateFromHours(visibleHoursStart, date).getHours();

  if (hour < startHour) {
    date.setDate(date.getDate() + 1);
  }

  const { visibleStart, visibleEnd } = getVisibleHoursBoundaries(
    visibleHoursStart,
    visibleHoursEnd,
    date
  );

  if (visibleStart.getDate() === visibleEnd.getDate()) {
    return day;
  }

  return formatDate(date);
}

export function getDefaultStartEnd(day, hour, { visibleHoursStart, visibleHoursEnd }) {
  const solarDay = getSolarDay(day, hour, { visibleHoursStart, visibleHoursEnd });
  const originalDay = getRealDate(day);
  let start = getRealDate(solarDay);
  start.setHours(9, 0, 0, 0);

  if (hour !== false && typeof hour !== 'undefined') {
    start.setHours(hour);
  }

  let end = addHours(start, 8);
  const { visibleStart, visibleEnd } = getBusinessDayForCalendar(
    visibleHoursStart,
    visibleHoursEnd,
    originalDay
  );

  if (start < visibleStart || start > visibleEnd) {
    start = visibleStart;
  }

  end = addHours(start, 8);
  if (end > visibleEnd) {
    end = visibleEnd;
  }

  return { start, end };
}

export function splitCamelCase(arg = '') {
  return arg
    .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim()
    .split(' ');
}

export function getSizeInMb(bytes) {
  return Math.round(bytes / 1000000);
}

export function setDate(to, from) {
  return getRealDate(`${formatDate(from)} ${formatTime(to, false, true)}`);
}

export function replaceArrayElement(array, index, element) {
  return Object.assign([], array, { [index]: element });
}

export function oneDeepCopy(obj) {
  const copy = { ...obj };

  Object.entries(copy).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      copy[key] = [...value];
    } else if (
      value &&
      typeof value === 'object' &&
      Object.prototype.toString.call(value) === '[object Object]'
    ) {
      copy[key] = { ...value };
    }
  });

  return copy;
}

export function getEmailSuggestion(email) {
  let emailSuggestion = null;

  mailcheck.run({
    email,
    domains: MAIL_CHECK_CONFIG.domains,
    topLevelDomains: MAIL_CHECK_CONFIG.topLevelDomains,
    secondLevelDomains: MAIL_CHECK_CONFIG.secondLevelDomains,
    suggested(suggestion) {
      emailSuggestion = suggestion.full;
    },
  });
  return emailSuggestion;
}

const DAY_NAMES_SHORT = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};
/**
 * @param {string} dateString in YYYY-MM-DD format.
 * @returns {string} in DDD D format (example: Mon 1, Fri 12).
 */
export function dateStringToDayDateString(dateString) {
  const date = getRealDate(dateString);
  const dayOfTheWeek = date.getDay();
  const dayOfTheMonth = date.getDate();

  return `${DAY_NAMES_SHORT[dayOfTheWeek]} ${dayOfTheMonth}`;
}

/**
 * @param {(string|Date)} dateOrString - either a date object or date string
 * @returns {Date} date object corresponding to the input argument
 */
export function toDateObject(dateOrString) {
  return typeof dateOrString === 'string' ? getRealDate(dateOrString) : dateOrString;
}

/**
 * @param {(string|Date)} dateOrString - either a date object or date string
 * @returns {string} date string corresponding to the input argument
 */
export function toDateString(dateOrString) {
  return typeof dateOrString === 'string' ? dateOrString.slice(0, 10) : formatDate(dateOrString);
}
